import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Typography,
  Col,
  Row,
  Upload,
  Button,
  Alert,
  Image,
  Skeleton,
  Progress,
} from "antd";
import { DeleteFilled } from "@ant-design/icons";
import styled from "styled-components";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { arrayMoveImmutable } from "utils";
import "./styles.css";
import { Shimmer } from "components/Shimmer";
import { gaEvent } from "analytics/analytics";
import * as GA from "../../../constants/analytics";

const { Title, Text } = Typography;

const MainPhoto = styled.div`
  span {
    display: flex;
    position: absolute;
    justify-content: center;
    bottom: 0;
    background: white;
    color: black;
    padding: 5px;
    border-radius: 40px;
    box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
    cursor: pointer;
  }
  &:hover {
    span {
      display: flex;
      position: absolute;
      justify-content: center;
      bottom: 0;
      background: white;
      color: black;
      padding: 5px;
      border-radius: 40px;
      box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.12);
      cursor: pointer;
    }
    div {
    }
  }
`;

const CELL_BORDER_RADIUS = 6;
const CELL_DIM = "12vh";
const INNER_BOX_WIDTH = "3vh";

const BottomTag = styled.div`
  border-bottom-right-radius: 6px;
  border-bottom-left-radius: 6px;
  width: 12vh;
  background: rgb(0, 0, 0, 0.7);
  color: white;
  display: flex;
  justify-content: flex-end;
  &:hover {
    background: white;
    color: black;
  }
`;

const SortableItem = SortableElement(
  ({ item, indexNum, onClickCell, isCurrentImage }) => {
    const [hover, setHover] = useState(false);
    const imageRef = useRef();
    const [loading, setLoading] = useState(true);

    const renderNumber = (indexNum) => {
      return (
        <span
          style={{
            background: "white",
            display: "flex",
            justifyContent: "center",
            height: INNER_BOX_WIDTH,
            width: INNER_BOX_WIDTH,
            borderRadius: "50%",
            margin: "0.6vh",
            fontSize: "0.7rem",
            alignItems: "center",
          }}
        >
          {indexNum}
        </span>
      );
    };

    const renderBottomTag = (tagName) => {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "4vh",
            width: CELL_DIM,

            alignItems: "center",
            padding: "0.1rem",
          }}
        >
          <span style={{ fontSize: "0.7rem", lineHeight: "normal" }}>
            {tagName}
          </span>
        </div>
      );
    };

    const handleOnLoad = function handleOnLoad() {
      setLoading(false);
    };

    const renderShimmer = () => {
      return (
        <div
          style={
            loading
              ? {
                  width: CELL_DIM,
                  height: CELL_DIM,
                  position: "absolute",
                  background: "red",
                  borderRadius: CELL_BORDER_RADIUS,
                }
              : { display: "none" }
          }
        >
          <Shimmer
            style={{
              width: "100%",
              height: "100%",
              borderRadius: CELL_BORDER_RADIUS,
            }}
          />
        </div>
      );
    };

    const boxStyle = {
      margin: "0.5vh",
      cursor: "pointer",
      listStyleType: "none",
      display: "inline-block",
      width: CELL_DIM,
      borderRadius: CELL_BORDER_RADIUS,
      height: CELL_DIM,
      boxShadow: "0 0 0 0",
    };

    return (
      <>
        <div
          onClick={() => {
            onClickCell(item?.directus_files_id);
            gaEvent(GA.EVENT.CLICK, GA.CATEGORY.ADD_PRODUCT, GA.DESCRIPTION.UPLOAD_IMAGE_UPLOADER);
          }}
          onMouseEnter={() => setHover(true)}
          onMouseLeave={() => setHover(false)}
          style={{
            margin: "0.5vh",
            cursor: "pointer",
            listStyleType: "none",
            display: "inline-block",
            width: CELL_DIM,
            borderRadius: CELL_BORDER_RADIUS,
            height: CELL_DIM,
            boxShadow: isCurrentImage ? "0 0 0 2pt black" : "0 0 0 0",
          }}
        >
          {renderShimmer()}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              width: CELL_DIM,
              height: CELL_DIM,
              position: "absolute",
            }}
          >
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {renderNumber(indexNum)}
            </div>

            <div
              style={{
                borderBottomRightRadius: 6,
                borderBottomLeftRadius: 6,
                width: CELL_DIM,
                background: hover ? "white" : "rgb(0, 0, 0, 0.7)",
                color: hover ? "black" : "white",

                display: "flex",
                justifyContent: "center",
              }}
            >
              {indexNum === 1 &&
                renderBottomTag(!hover ? "Main" : "Drag to rearrage")}
              {indexNum === 2 &&
                renderBottomTag(
                  !hover ? "Secondary" : "Drag to rearrage"
                )}
              {indexNum > 2 && hover && renderBottomTag("Drag to rearrage")}
            </div>
          </div>

          <img
            ref={imageRef}
            alt="prodcuts"
            width={"100%"}
            style={
              loading
                ? { visibility: "hidden", height: CELL_DIM }
                : { borderRadius: 6 }
            }
            src={`${process.env.REACT_APP_DIRECTUS}assets/${item?.directus_files_id}?transforms=[["resize",{"width":600,"height":600,"fit":"contain","background":{"r":242,"g":242,"b":242}}]]`}
            onLoad={handleOnLoad}
          />
        </div>
      </>
    );
  }
);

const SortableList = SortableContainer(
  ({ items, onClickCell, currentImage }) => {
    return (
      <div>
        {items.map((item, index) => {
          return (
            <>
              <SortableItem
                key={`item-${item.uid}`}
                index={index}
                indexNum={index + 1}
                item={item}
                onClickCell={onClickCell}
                isCurrentImage={currentImage === item?.directus_files_id}
              />
            </>
          );
        })}
      </div>
    );
  }
);

export default function ImageUploadModal({
  isModalVisible,
  showModal,
  handleOk,
  handleCancel,
  handlePreview,
  photoList,
  setphotoList,
  uploadImage,
  handleChangeUpload,
  progress,
  error,
  beforeUpload,
  onRemove,
}) {
  const [currentImage, setCurrentImage] = useState(null);
  const [imageIndexRemoved, setImageIndexRemoved] = useState(null);
  const [imageLoaded, setImageLoaded] = useState(null);
  const [imageGridLoading, setImageGridLoading] = useState(false);

  useEffect(() => {
    if (photoList?.length > 0 && imageIndexRemoved !== null) {
      setCurrentImage(photoList?.[imageIndexRemoved]?.directus_files_id);
      setImageIndexRemoved(null);
      return;
    }
    if (photoList?.length > 0) {
      setCurrentImage(photoList?.[photoList.length - 1]?.directus_files_id);
      return;
    }
  }, [photoList]);

  const handleRemove = (imageId) => {
    let imageIndexToRemove;
    const newPhotoList = photoList.filter((image, index) => {
      if (image.directus_files_id === imageId) {
        imageIndexToRemove = index;
      }
      return image.directus_files_id !== imageId;
    });

    if (imageIndexToRemove < newPhotoList.length)
      setImageIndexRemoved(imageIndexToRemove);

    setphotoList(newPhotoList);
  };

  const handleOnClickImageCell = (id) => {
    setCurrentImage(id);
  };

  const renderInitalBox = () => {
    return (
      <>
        {progress === 0 && photoList.length === 0 && (
          <Col>
            <Title level={4}>No images uploaded</Title>
          </Col>
        )}
        <Col>
          {progress > 0 && photoList.length === 0 && (
            <Progress type="circle" style={{}} percent={progress} width={80} />
          )}
        </Col>
      </>
    );
  };

  const renderLoadingHeader = () => {
    if (progress > 0) {
      return (
        <Row style={{ height: "2vh", marginBottom: "1vh" }}>
          <Progress
            style={{
              marginTop: "0.5rem",
            }}
            percent={progress}
            width={"2vh"}
          />
        </Row>
      );
    }
    return <Row style={{ height: "2vh", marginBottom: "1vh" }}></Row>;
  };

  const renderTag = (index, tagName) => {
    if (photoList?.[index]?.directus_files_id === currentImage) {
      return (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            marginBottom: -20,
          }}
        >
          {" "}
          <span
            className="main-btn"
            style={{
              borderRadius: 0,
              padding: 10,
              borderRadius: 40,
              fontSize: 14,
              cursor: "default",
            }}
          >
            {tagName}
          </span>
        </div>
      );
    }
  };

  const onDragEnd = (result) => {
    const sourceIndex = result?.source?.index;
    const destinationIndex = result?.destination?.index;

    if (
      typeof sourceIndex === "number" &&
      typeof destinationIndex === "number"
    ) {
      const newPhotoList = photoList.slice();
      const temp = newPhotoList[sourceIndex];
      newPhotoList[sourceIndex] = newPhotoList[destinationIndex];
      newPhotoList[destinationIndex] = temp;
      setphotoList(newPhotoList);
    }
  };

  const onSortEnd = ({ oldIndex, newIndex }) => {
    // const sourceIndex = result?.source?.index;
    // const destinationIndex = result?.destination?.index;
    const newPhotoList = arrayMoveImmutable(photoList, oldIndex, newIndex);

    setphotoList(newPhotoList);
  };

  return (
    <>
      <Modal
        width={"120vh"}
        visible={isModalVisible}
        footer={null}
        onOk={handleOk}
        onCancel={handleCancel}
        bodyStyle={{ padding: "2rem" }}
      >
        <Row>
          <Col>
            <Title level={4}>Image uploader</Title>
          </Col>
        </Row>

        <Row>
          <Text type="secondary">
            You can select the main photo and rearrage the order of the images
          </Text>
        </Row>
        {renderLoadingHeader()}

        <Row>
          <Col>{error && <Alert type="error" message={error} banner />}</Col>
        </Row>

        {photoList.length > 0 ? (
          <Row style={{ marginTop: "1rem" }}>
            <Col span={12}>
              <MainPhoto>
                <Image
                  className="main-image"
                  src={`${process.env.REACT_APP_DIRECTUS}assets/${
                    currentImage || photoList?.[0]?.directus_files_id
                  }?transforms=[["resize",{"width":336,"height":300,"fit":"contain","background":{"r":242,"g":242,"b":242}}]]`}
                  alt="product main"
                  width="56vh"
                  height="50vh"
                  style={{ borderRadius: 12 }}
                  preview={false}
                />

                {renderTag(0, "Main Photo")}
                {renderTag(1, "Secondary Photo")}
              </MainPhoto>
              {
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    flexDirection: "row",
                    alignContent: "center",
                  }}
                >
                  <DeleteFilled
                    onClick={() => {
                      handleRemove(
                        currentImage || photoList?.[0]?.directus_files_id
                      )
                      gaEvent(GA.EVENT.CLICK, GA.CATEGORY.ADD_PRODUCT, GA.DESCRIPTION.UPLOAD_IMAGE_REMOVE)
                    }}
                    style={{
                      position: "absolute",
                      color: "white",
                      padding: 10,
                      fontSize: 24,
                      top: 15,
                      right: 15,
                      cursor: "pointer",
                      background: "rgb(0,0,0,0.7)",

                      borderRadius: "50%",
                    }}
                  />
                </div>
              }
            </Col>
            <Col span={11} offset={1}>
              <SortableList
                axis="xy"
                items={photoList}
                onSortEnd={onSortEnd}
                onClickCell={handleOnClickImageCell}
                distance={1}
                currentImage={currentImage}
              />
            </Col>
          </Row>
        ) : (
          <Row style={{ marginTop: "1rem" }}>
            <Col span={12}>
              <MainPhoto>
                <Skeleton.Image
                  style={{
                    width: "56vh",
                    height: "50vh",
                    borderRadius: 12,
                    opacity: 0.5,
                  }}
                  className="main-image"
                />
              </MainPhoto>
            </Col>
          </Row>
        )}

        <Row style={{ marginTop: "3.5rem" }} justify="space-between">
          <Col span={4}>
            <Upload
              className="upload-more-images"
              onPreview={handlePreview}
              customRequest={uploadImage}
              beforeUpload={beforeUpload}
              onChange={handleChangeUpload}
              onRemove={onRemove}
              showUploadList={false}
              multiple
              onClick={() => {
                gaEvent(GA.EVENT.CLICK, GA.CATEGORY.ADD_PRODUCT, GA.DESCRIPTION.UPLOAD_IMAGE_CONTAINER)
              }}
            >
              <Button
                size="large"
                style={{ width: "100%" }}
                disabled={progress > 0}
                onClick={() => {
                  gaEvent(GA.EVENT.CLICK, GA.CATEGORY.ADD_PRODUCT, GA.DESCRIPTION.UPLOAD_IMAGE_MORE_BUTTON);
                }}
              >
                Upload more images
              </Button>
            </Upload>
          </Col>
          <Col span={4}>
            <Button
              onClick={() => {
                handleOk()
                gaEvent(GA.EVENT.CLICK, GA.CATEGORY.ADD_PRODUCT, GA.DESCRIPTION.UPLOAD_IMAGE_DONE_BUTTON);
              }}
              size="large"
              style={{ width: "100%" }}
              type="primary"
            >
              Done
            </Button>
          </Col>
        </Row>
      </Modal>
    </>
  );
}
